import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import EvaluationReport from './EvaluationReport';
import { doc, deleteDoc, getDoc, collection, getDocs, updateDoc, query, orderBy, limit as firestoreLimit } from 'firebase/firestore';
import { db } from '../../firebaseInit';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminPreview } from '../../contexts/AdminPreviewContext';
import { useData } from '../../contexts/DataContext';
import { useNotification } from '../../contexts/NotificationContext';
import { exportEvaluationToPDF } from './pdfExport';
import LoadingSpinner from '../shared/LoadingSpinner';
import Button from '../shared/Button';
import ConfirmDialog from '../shared/ConfirmDialog';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getCallTypes, getCallTypeLabel as getCallTypeLabelFromConfig, formatUserName } from '../../utils/industryConfig';
import { createAutoReport } from '../../utils/autoProblemReport';

// Score range presets
const SCORE_RANGES = [
  { value: 'all', label: 'All Scores' },
  { value: '90-100', label: 'Excellent (90-100)', min: 90, max: 100 },
  { value: '80-89', label: 'Good (80-89)', min: 80, max: 89 },
  { value: '70-79', label: 'Fair (70-79)', min: 70, max: 79 },
  { value: '60-69', label: 'Needs Work (60-69)', min: 60, max: 69 },
  { value: '0-59', label: 'Poor (Below 60)', min: 0, max: 59 }
];

function getDisplayName(evaluation) {
  if (evaluation.salespersonName && evaluation.salespersonName !== evaluation.salesperson) {
    return evaluation.salespersonName;
  }
  return formatUserName({ email: evaluation.salesperson });
}

export default function EvaluationList({ evaluations, loading, onRefresh, initialSelectedId, onClearSelection }) {
  const { userTeam, userEmail } = useAuth();
  const { effectiveUserRole: userRole } = useAdminPreview();
  const { teamData } = useData();
  const { showSuccess, showError, showWarning, showInfo } = useNotification();
  const industryId = teamData?.industryId;
  const CALL_TYPES = getCallTypes(industryId);
  const [selectedEval, setSelectedEval] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [reEvaluationProgress, setReEvaluationProgress] = useState({ 
    isRunning: false, 
    current: 0, 
    total: 0, 
    message: '' 
  });
  
  // Filter state
  const [filters, setFilters] = useState({
    salesperson: '',
    callType: '',
    scoreRange: 'all',
    dateFrom: '',
    dateTo: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Sort state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteEval, setPendingDeleteEval] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: 'uploadedAt',
    direction: 'desc'
  });
  
  const isAdmin = userRole === 'admin';

  // Lead linking state
  const [linkingEvalId, setLinkingEvalId] = useState(null); // which eval row has picker open
  const [recentLeads, setRecentLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [linkingInProgress, setLinkingInProgress] = useState(false);
  const [linkedLeadNames, setLinkedLeadNames] = useState({}); // leadId → name
  const leadPickerRef = useRef(null);

  // Load lead names for all evaluations that have linkedLeadId
  useEffect(() => {
    if (!userTeam || !db || evaluations.length === 0) return;
    const linkedIds = evaluations
      .filter(e => e.linkedLeadId && !e.linkedLeadName)
      .map(e => e.linkedLeadId);
    if (linkedIds.length === 0) return;

    let mounted = true;
    const loadNames = async () => {
      try {
        const teamUsersPath = `artifacts/my-real-estate-assistant/teams/${userTeam}/users`;
        const usersSnapshot = await getDocs(collection(db, teamUsersPath));
        const namesMap = {};
        const remaining = new Set(linkedIds);
        for (const userDoc of usersSnapshot.docs) {
          if (remaining.size === 0) break;
          const leadsPath = `${teamUsersPath}/${userDoc.id}/realEstateLeads`;
          const leadsSnapshot = await getDocs(collection(db, leadsPath));
          leadsSnapshot.docs.forEach(leadDoc => {
            if (remaining.has(leadDoc.id)) {
              const data = leadDoc.data();
              namesMap[leadDoc.id] = data.prospectName || data.prospectPhone || 'Unnamed';
              remaining.delete(leadDoc.id);
            }
          });
        }
        if (mounted) setLinkedLeadNames(prev => ({ ...prev, ...namesMap }));
      } catch (error) {
        console.warn('Failed to load linked lead names:', error);
      }
    };
    loadNames();
    return () => { mounted = false; };
  }, [userTeam, evaluations]);

  // Close lead picker when clicking outside
  useEffect(() => {
    if (!linkingEvalId) return;
    const handleClickOutside = (e) => {
      if (leadPickerRef.current && !leadPickerRef.current.contains(e.target)) {
        setLinkingEvalId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [linkingEvalId]);

  // Load recent leads when picker opens
  useEffect(() => {
    if (!linkingEvalId || !userTeam || !db) return;
    let mounted = true;
    const loadLeads = async () => {
      setLoadingLeads(true);
      try {
        const teamUsersPath = `artifacts/my-real-estate-assistant/teams/${userTeam}/users`;
        const usersSnapshot = await getDocs(collection(db, teamUsersPath));
        const allLeads = [];
        for (const userDoc of usersSnapshot.docs) {
          const leadsPath = `${teamUsersPath}/${userDoc.id}/realEstateLeads`;
          const leadsQuery = query(collection(db, leadsPath), orderBy('timestamp', 'desc'), firestoreLimit(20));
          const leadsSnapshot = await getDocs(leadsQuery);
          leadsSnapshot.docs.forEach(leadDoc => {
            const data = leadDoc.data();
            allLeads.push({
              id: leadDoc.id,
              path: `${leadsPath}/${leadDoc.id}`,
              userDocId: userDoc.id,
              ...data,
              timestamp: data.timestamp?.toDate?.() || (data.timestamp ? new Date(data.timestamp) : null)
            });
          });
        }
        allLeads.sort((a, b) => (b.timestamp?.getTime() || 0) - (a.timestamp?.getTime() || 0));
        if (mounted) setRecentLeads(allLeads.slice(0, 50));
      } catch (error) {
        console.warn('Failed to load leads for linking:', error);
      } finally {
        if (mounted) setLoadingLeads(false);
      }
    };
    loadLeads();
    return () => { mounted = false; };
  }, [linkingEvalId, userTeam]);

  // Link a lead to an evaluation (bidirectional)
  const handleLinkLeadToEval = useCallback(async (evalId, leadId) => {
    if (!db || !userTeam || !evalId || !leadId || linkingInProgress) return;
    setLinkingInProgress(true);
    try {
      const selectedLead = recentLeads.find(l => l.id === leadId);
      const leadName = selectedLead?.prospectName || selectedLead?.prospectPhone || 'Unnamed';
      // Update evaluation with lead link + cached name
      const evalRef = doc(db, `artifacts/my-real-estate-assistant/teams/${userTeam}/call-evaluations/${evalId}`);
      await updateDoc(evalRef, { linkedLeadId: leadId, linkedLeadName: leadName });
      // Update lead with evaluation link
      if (selectedLead?.path) {
        await updateDoc(doc(db, selectedLead.path), { linkedEvaluationId: evalId });
      }
      // Update local name map immediately
      setLinkedLeadNames(prev => ({ ...prev, [leadId]: leadName }));
      setLinkingEvalId(null);
      showSuccess('Lead linked to evaluation');
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to link lead:', err);
      showError('Could not link lead. Please try again.');
    } finally {
      setLinkingInProgress(false);
    }
  }, [db, userTeam, recentLeads, linkingInProgress, showSuccess, showError, onRefresh]);

  // Unlink a lead from an evaluation (bidirectional)
  const handleUnlinkLeadFromEval = useCallback(async (evaluation) => {
    if (!db || !userTeam || linkingInProgress) return;
    const leadId = evaluation.linkedLeadId;
    if (!leadId) return;
    setLinkingInProgress(true);
    try {
      // Remove lead link from evaluation
      const evalRef = doc(db, `artifacts/my-real-estate-assistant/teams/${evaluation.teamId || userTeam}/call-evaluations/${evaluation.id}`);
      await updateDoc(evalRef, { linkedLeadId: null });
      // Find and unlink the lead
      const teamUsersPath = `artifacts/my-real-estate-assistant/teams/${userTeam}/users`;
      const usersSnapshot = await getDocs(collection(db, teamUsersPath));
      for (const userDoc of usersSnapshot.docs) {
        const leadsPath = `${teamUsersPath}/${userDoc.id}/realEstateLeads`;
        const leadsSnapshot = await getDocs(collection(db, leadsPath));
        const match = leadsSnapshot.docs.find(d => d.id === leadId);
        if (match) {
          await updateDoc(doc(db, `${leadsPath}/${match.id}`), { linkedEvaluationId: null });
          break;
        }
      }
      showSuccess('Lead unlinked from evaluation');
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to unlink lead:', err);
      showError('Could not unlink lead. Please try again.');
    } finally {
      setLinkingInProgress(false);
    }
  }, [db, userTeam, linkingInProgress, showSuccess, showError, onRefresh]);

  // Handle initial selection from Analytics view
  useEffect(() => {
    if (initialSelectedId && evaluations.length > 0) {
      const evaluation = evaluations.find(e => e.id === initialSelectedId);
      if (evaluation) {
        setSelectedEval(evaluation);
        // Clear the selection after using it
        if (onClearSelection) {
          onClearSelection();
        }
      }
    }
  }, [initialSelectedId, evaluations, onClearSelection]);
  const functions = getFunctions();

  // Get unique salespersons for filter dropdown (email -> display name mapping)
  const uniqueSalespersons = useMemo(() => {
    const seen = new Map();
    evaluations.forEach(e => {
      if (e.salesperson && !seen.has(e.salesperson)) {
        seen.set(e.salesperson, getDisplayName(e));
      }
    });
    return [...seen.entries()]
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([email, name]) => ({ email, name }));
  }, [evaluations]);

  // Filter and sort evaluations
  const filteredAndSortedEvaluations = useMemo(() => {
    let result = [...evaluations];

    // Apply filters
    if (filters.salesperson) {
      result = result.filter(e => e.salesperson === filters.salesperson);
    }

    if (filters.callType) {
      result = result.filter(e => e.callType === filters.callType);
    }

    if (filters.scoreRange && filters.scoreRange !== 'all') {
      const range = SCORE_RANGES.find(r => r.value === filters.scoreRange);
      if (range) {
        result = result.filter(e => {
          const score = e.evaluation?.overallScore;
          return score !== undefined && score >= range.min && score <= range.max;
        });
      }
    }

    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      result = result.filter(e => {
        const evalDate = e.uploadedAt?.toDate ? e.uploadedAt.toDate() : new Date(e.uploadedAt);
        return evalDate >= fromDate;
      });
    }

    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      result = result.filter(e => {
        const evalDate = e.uploadedAt?.toDate ? e.uploadedAt.toDate() : new Date(e.uploadedAt);
        return evalDate <= toDate;
      });
    }

    // Apply sorting
    result.sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'uploadedAt':
          aValue = a.uploadedAt?.toMillis?.() || a.uploadedAt?.getTime?.() || 0;
          bValue = b.uploadedAt?.toMillis?.() || b.uploadedAt?.getTime?.() || 0;
          break;
        case 'salesperson':
          aValue = (a.salesperson || '').toLowerCase();
          bValue = (b.salesperson || '').toLowerCase();
          break;
        case 'prospect':
          aValue = (a.prospect || '').toLowerCase();
          bValue = (b.prospect || '').toLowerCase();
          break;
        case 'callType':
          aValue = a.callType || '';
          bValue = b.callType || '';
          break;
        case 'score':
          aValue = a.evaluation?.overallScore ?? -1;
          bValue = b.evaluation?.overallScore ?? -1;
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [evaluations, filters, sortConfig]);

  // Handle sort column click
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get sort indicator for column
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) {
      return <span className="text-gray-300 ml-1">↕</span>;
    }
    return sortConfig.direction === 'asc' 
      ? <span className="text-blue-600 ml-1">↑</span>
      : <span className="text-blue-600 ml-1">↓</span>;
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      salesperson: '',
      callType: '',
      scoreRange: 'all',
      dateFrom: '',
      dateTo: ''
    });
  };

  // Check if any filters are active
  const hasActiveFilters = filters.salesperson || filters.callType || filters.scoreRange !== 'all' || filters.dateFrom || filters.dateTo;

  const handleEvaluationUpdate = async () => {
    // Refresh the selected evaluation from Firestore
    if (selectedEval && selectedEval.id && selectedEval.teamId) {
      try {
        const evalRef = doc(db, `artifacts/my-real-estate-assistant/teams/${selectedEval.teamId}/call-evaluations/${selectedEval.id}`);
        const evalDoc = await getDoc(evalRef);
        if (evalDoc.exists()) {
          setSelectedEval({ id: evalDoc.id, ...evalDoc.data() });
        }
      } catch (error) {
        console.error('Error refreshing evaluation:', error);
      }
    }
  };

  const handleSelectAll = () => {
    const visibleIds = filteredAndSortedEvaluations.map(e => e.id);
    if (selectedIds.length === visibleIds.length && visibleIds.every(id => selectedIds.includes(id))) {
      setSelectedIds([]);
    } else {
      setSelectedIds(visibleIds);
    }
  };

  const handleSelectOne = (evalId) => {
    setSelectedIds(prev => 
      prev.includes(evalId) 
        ? prev.filter(id => id !== evalId)
        : [...prev, evalId]
    );
  };

  const handleDelete = useCallback((evaluation) => {
    setPendingDeleteEval(evaluation);
    setShowDeleteConfirm(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!pendingDeleteEval) return;
    
    const evaluation = pendingDeleteEval;
    setShowDeleteConfirm(false);
    setPendingDeleteEval(null);
    setDeletingId(evaluation.id);
    
    try {
      // Use team container path
      const evalPath = `artifacts/my-real-estate-assistant/teams/${evaluation.teamId || userTeam}/call-evaluations`;
      const evalRef = doc(db, evalPath, evaluation.id);
      
      await deleteDoc(evalRef);
      
      console.log('✅ Evaluation deleted:', evaluation.id);
      showSuccess('Evaluation deleted successfully');
    } catch (error) {
      console.error('Error deleting evaluation:', error);
      showError('Couldn\'t delete this evaluation. Please try again.');
    } finally {
      setDeletingId(null);
    }
  }, [pendingDeleteEval, userTeam, showSuccess, showError]);

  const handleBulkReEvaluate = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (selectedIds.length === 0) {
      showWarning('Please select at least one evaluation to re-evaluate');
      return;
    }

    // Filter out expired evaluations
    const selectedEvals = evaluations.filter(e => selectedIds.includes(e.id));
    const now = new Date();
    const expiredEvals = selectedEvals.filter(e => {
      if (!e.expiresAt) return false;
      const expireDate = e.expiresAt.toDate ? e.expiresAt.toDate() : new Date(e.expiresAt);
      return now > expireDate;
    });

    if (expiredEvals.length > 0) {
      showError(`Cannot re-evaluate ${expiredEvals.length} expired evaluation(s). Please deselect expired items and try again.`);
      return;
    }

    const total = selectedIds.length;
    let progressInterval = null;

    try {
      console.log(`🔄 Starting bulk re-evaluation of ${total} calls...`);
      console.log('Selected IDs:', selectedIds);
      console.log('Team ID:', userTeam);
      
      // Log the actual evaluation objects to see their teamId
      console.log('Selected evaluation objects:', selectedEvals);
      console.log('Evaluation teamIds:', selectedEvals.map(e => ({ id: e.id, teamId: e.teamId })));
      
      // Use the actual teamId from the evaluations (not userTeam which might be admin's team)
      const actualTeamId = selectedEvals[0]?.teamId || userTeam;
      console.log('Using teamId:', actualTeamId, '(userTeam was:', userTeam + ')');
      
      // Verify all evaluations are from the same team
      const allSameTeam = selectedEvals.every(e => e.teamId === actualTeamId);
      if (!allSameTeam) {
        showError('Cannot re-evaluate calls from different teams at once');
        return;
      }
      
      setReEvaluationProgress({ 
        isRunning: true, 
        current: 0, 
        total, 
        message: `Starting re-evaluation of ${total} call${total > 1 ? 's' : ''}...` 
      });
      
      showInfo(`Starting bulk re-evaluation of ${total} call${total > 1 ? 's' : ''}. This will run in the background.`);

      // Simulate progress updates (since we can't get real-time progress from Cloud Function)
      progressInterval = setInterval(() => {
        setReEvaluationProgress(prev => {
          if (!prev.isRunning || prev.current >= prev.total) {
            return prev;
          }
          const newCurrent = Math.min(prev.current + 1, prev.total);
          const percent = Math.round((newCurrent / prev.total) * 100);
          return {
            ...prev,
            current: newCurrent,
            message: `Processing call ${newCurrent} of ${prev.total} (${percent}%)...`
          };
        });
      }, 3000); // Update every 3 seconds

      // Call Cloud Function with extended timeout (10 minutes for bulk processing)
      const reEvaluateCalls = httpsCallable(functions, 'reEvaluateCalls', {
        timeout: 600000 // 10 minutes in milliseconds
      });
      
      console.log('📞 Calling reEvaluateCalls cloud function...');
      const result = await reEvaluateCalls({
        evaluationIds: selectedIds,
        teamId: actualTeamId  // Use the evaluation's actual teamId
      });

      console.log('✅ Cloud function response:', result);
      console.log('✅ Response data:', result.data);
      console.log('✅ Response data type:', typeof result.data);
      console.log('✅ Success count:', result.data?.success);
      console.log('✅ Failed count:', result.data?.failed);
      console.log('✅ Errors:', result.data?.errors);
      
      // Clear progress interval
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }

      // Check results
      const data = result.data;
      
      // Log error details if any
      if (data.errors && data.errors.length > 0) {
        console.error('❌ Re-evaluation errors:');
        data.errors.forEach(err => {
          console.error(`  - ${err.evalId}: ${err.error}`);
        });
      }
      
      if (data.success > 0) {
        setReEvaluationProgress({ 
          isRunning: false, 
          current: total, 
          total, 
          message: 'Complete!' 
        });
        
        const successMsg = `Successfully re-evaluated ${data.success} call${data.success > 1 ? 's' : ''}!`;
        const failMsg = data.failed > 0 ? ` (${data.failed} failed)` : '';
        showSuccess(successMsg + failMsg);
        
        console.log(`✅ Re-evaluation complete: ${data.success} success, ${data.failed} failed`);
        
        if (data.scoreChanges && data.scoreChanges.length > 0) {
          console.log('📊 Score changes:');
          data.scoreChanges.forEach(change => {
            console.log(`  ${change.salesperson}: ${change.oldScore} → ${change.newScore} (${change.diff > 0 ? '+' : ''}${change.diff})`);
          });
        }
        
        setSelectedIds([]);
        
        // Refresh evaluations data without page reload
        if (onRefresh) {
          setTimeout(() => {
            onRefresh();
          }, 1500);
        }
      } else {
        throw new Error(`All ${data.total} re-evaluations failed`);
      }

    } catch (error) {
      console.error('❌ Error in bulk re-evaluation:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        details: error.details
      });
      
      // Clear progress interval
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
      
      setReEvaluationProgress({ 
        isRunning: false, 
        current: 0, 
        total: 0, 
        message: 'Failed' 
      });
      
      showError('Something went wrong during re-evaluation. Please try again.');
      createAutoReport({
        errorType: 'bulk_re_evaluation_failed',
        errorMessage: error.message,
        userEmail,
        userRole,
        teamId: userTeam,
        context: {
          evaluationIds: selectedIds,
          evaluationCount: selectedIds.length,
          errorCode: error.code,
        },
      });
    }
  };
  
  if (loading) {
    return (
      <div className="text-center py-8">
        <LoadingSpinner size="large" message="Loading evaluations..." />
      </div>
    );
  }
  
  if (evaluations.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-6xl mb-3">📞</div>
        <h3 className="text-xl font-semibold text-gray-700 mb-2">No evaluations yet</h3>
        <p className="text-gray-500">Upload a call recording to get started!</p>
      </div>
    );
  }
  
  const getCallTypeLabel = (callType) => {
    return getCallTypeLabelFromConfig(callType, industryId);
  };

  const getStatusBadge = (status) => {
    const badges = {
      'uploading': <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">Uploading</span>,
      'transcribing': <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Transcribing</span>,
      'analyzing': <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">Analyzing</span>,
      'completed': <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Completed</span>,
      'error': <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Error</span>,
    };
    return badges[status] || <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">{status}</span>;
  };
  
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    // Show relative time with full date on hover
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHrs = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    let relative;
    if (diffMins < 1) relative = 'just now';
    else if (diffMins < 60) relative = `${diffMins}m ago`;
    else if (diffHrs < 24) relative = `${diffHrs}h ago`;
    else if (diffDays < 7) relative = `${diffDays}d ago`;
    else relative = date.toLocaleDateString();
    return relative;
  };

  const formatDateFull = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysRemaining = (expiresAt) => {
    if (!expiresAt) return null;
    const expireDate = expiresAt.toDate ? expiresAt.toDate() : new Date(expiresAt);
    const now = new Date();
    const diffMs = expireDate - now;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getExpirationBadge = (daysRemaining) => {
    if (daysRemaining === null) return <span className="text-xs text-gray-400">No expiration</span>;
    
    if (daysRemaining <= 0) {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Expired</span>;
    } else if (daysRemaining <= 7) {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
          {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}
        </span>
      );
    } else if (daysRemaining <= 14) {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
          {daysRemaining} days
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
          {daysRemaining} days
        </span>
      );
    }
  };
  
  const handleExportIndividual = (evaluation) => {
    if (!evaluation.evaluation) {
      showWarning('No evaluation data available');
      return;
    }
    exportEvaluationToPDF(evaluation, formatDateFull);
  };

  const handleExportCSV = () => {
    // Filter only completed evaluations with scores (from filtered list if filters active)
    const sourceEvals = hasActiveFilters ? filteredAndSortedEvaluations : evaluations;
    const completedEvals = sourceEvals.filter(e => e.status === 'completed' && e.evaluation);
    
    if (completedEvals.length === 0) {
      showInfo('No completed evaluations to export');
      return;
    }
    
    // CSV Header
    const headers = [
      'Date',
      'Salesperson',
      'Prospect',
      'Call Type',
      'Overall Score',
      'Opening & Control',
      'Empathy & Curiosity',
      'Emotional Engagement & Certainty',
      'Demonstrating Non-Neediness',
      'Stories vs Explaining',
      'Assertive vs Aggressive',
      'Guiding Self-Sell',
      'Objection Handling',
      'Closing & Buy-In',
      'Next Steps',
      'Prospect Talk %',
      'Salesperson Talk %'
    ];
    
    // CSV Rows
    const rows = completedEvals.map(evaluation => {
      const eval_data = evaluation.evaluation;
      const categories = eval_data.categoryScores || [];
      
      return [
        formatDate(evaluation.uploadedAt),
        getDisplayName(evaluation),
        evaluation.prospect,
        evaluation.callType,
        eval_data.overallScore || '',
        categories[0]?.score || '',
        categories[1]?.score || '',
        categories[2]?.score || '',
        categories[3]?.score || '',
        categories[4]?.score || '',
        categories[5]?.score || '',
        categories[6]?.score || '',
        categories[7]?.score || '',
        categories[8]?.score || '',
        categories[9]?.score || '',
        eval_data.talkingRatio?.prospect || '',
        eval_data.talkingRatio?.salesperson || ''
      ];
    });
    
    // Convert to CSV string
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => {
        // Escape commas and quotes in cell values
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(','))
    ].join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `call-evaluations-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log(`✅ Exported ${completedEvals.length} evaluations to CSV`);
  };
  
  return (
    <div>
      {/* Progress Bar */}
      {reEvaluationProgress.isRunning && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-sm font-medium text-blue-900">{reEvaluationProgress.message}</span>
            </div>
            <span className="text-sm text-blue-700">
              {reEvaluationProgress.current}/{reEvaluationProgress.total}
            </span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2.5">
            <div 
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
              style={{ width: `${(reEvaluationProgress.current / reEvaluationProgress.total) * 100}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="mb-4 flex justify-between items-center">
        <div className="flex items-center space-x-3">
          {/* Bulk Re-Evaluate Button */}
          {isAdmin && selectedIds.length > 0 && (
            <Button
              variant="primary"
              size="md"
              onClick={handleBulkReEvaluate}
              disabled={reEvaluationProgress.isRunning}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              }
            >
              Re-Evaluate Selected ({selectedIds.length})
            </Button>
          )}
        </div>

        {/* Export Button */}
        {evaluations.filter(e => e.status === 'completed' && e.evaluation).length > 0 && (
          <Button
            variant="secondary"
            size="md"
            onClick={handleExportCSV}
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
          >
            Export to CSV
          </Button>
        )}
      </div>

      {/* Filter Controls */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
            }
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
            {hasActiveFilters && (
              <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full ml-2">
                Active
              </span>
            )}
          </Button>
          
          <div className="text-sm text-gray-500">
            Showing {filteredAndSortedEvaluations.length} of {evaluations.length} evaluations
          </div>
        </div>

        {showFilters && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Salesperson Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Salesperson
                </label>
                <select
                  value={filters.salesperson}
                  onChange={(e) => setFilters(prev => ({ ...prev, salesperson: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Salespersons</option>
                  {uniqueSalespersons.map(sp => (
                    <option key={sp.email} value={sp.email}>{sp.name}</option>
                  ))}
                </select>
              </div>

              {/* Call Type Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Call Type
                </label>
                <select
                  value={filters.callType}
                  onChange={(e) => setFilters(prev => ({ ...prev, callType: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Call Types</option>
                  {CALL_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Score Range Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Score Range
                </label>
                <select
                  value={filters.scoreRange}
                  onChange={(e) => setFilters(prev => ({ ...prev, scoreRange: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {SCORE_RANGES.map(range => (
                    <option key={range.value} value={range.value}>{range.label}</option>
                  ))}
                </select>
              </div>

              {/* Date From Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  From Date
                </label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Date To Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  To Date
                </label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <div className="flex justify-end">
                <button
                  onClick={clearFilters}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Clear All Filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Scrollable table container */}
      <div className="bg-white rounded-lg shadow overflow-auto max-h-[calc(100vh-380px)]">
        <table className="w-full divide-y divide-gray-200 table-fixed">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              {isAdmin && (
                <th className="w-10 px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={selectedIds.length === filteredAndSortedEvaluations.length && filteredAndSortedEvaluations.length > 0}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                    title="Select all visible"
                  />
                </th>
              )}
              <th 
                className="w-32 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('uploadedAt')}
              >
                <div className="flex items-center">
                  Date
                  {getSortIndicator('uploadedAt')}
                </div>
              </th>
              <th 
                className="w-44 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('salesperson')}
              >
                <div className="flex items-center">
                  Salesperson
                  {getSortIndicator('salesperson')}
                </div>
              </th>
              <th 
                className="w-36 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('prospect')}
              >
                <div className="flex items-center">
                  Prospect
                  {getSortIndicator('prospect')}
                </div>
              </th>
              <th 
                className="w-28 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('callType')}
              >
                <div className="flex items-center">
                  Type
                  {getSortIndicator('callType')}
                </div>
              </th>
              <th 
                className="w-20 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('score')}
              >
                <div className="flex items-center">
                  Score
                  {getSortIndicator('score')}
                </div>
              </th>
              <th 
                className="w-24 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center">
                  Status
                  {getSortIndicator('status')}
                </div>
              </th>
              <th className="w-36 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Lead
              </th>
              <th className="w-36 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
              <th className="w-24 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Expires
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredAndSortedEvaluations.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? 10 : 9} className="px-4 py-4 text-center text-gray-500">
                  <div className="text-4xl mb-2">🔍</div>
                  <p className="font-medium">No evaluations match your filters</p>
                  <button
                    onClick={clearFilters}
                    className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Clear Filters
                  </button>
                </td>
              </tr>
            ) : (
              filteredAndSortedEvaluations.map((evaluation) => (
                <tr key={evaluation.id} className="hover:bg-gray-50">
                  {isAdmin && (
                    <td className="px-2 py-2">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(evaluation.id)}
                        onChange={() => handleSelectOne(evaluation.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                      />
                    </td>
                  )}
                  <td className="px-3 py-2 text-sm text-gray-900" title={formatDateFull(evaluation.uploadedAt)}>
                    {formatDate(evaluation.uploadedAt)}
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-sm font-medium text-gray-900 truncate" title={evaluation.salesperson}>{getDisplayName(evaluation)}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-sm text-gray-900 truncate" title={evaluation.prospect}>{evaluation.prospect}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      CALL_TYPES.findIndex(ct => ct.value === evaluation.callType) === 0 ? 'bg-purple-100 text-purple-800' :
                        CALL_TYPES.findIndex(ct => ct.value === evaluation.callType) === 1 ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                    }`}>
                      {getCallTypeLabel(evaluation.callType)}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {evaluation.status === 'completed' && evaluation.evaluation ? (
                      <div className="flex items-center">
                        <span className="text-lg font-bold text-blue-600">
                          {evaluation.evaluation.overallScore}
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {getStatusBadge(evaluation.status)}
                  </td>
                  {/* Linked Lead indicator — interactive */}
                  <td className="px-3 py-2 relative">
                    {evaluation.linkedLeadId ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleUnlinkLeadFromEval(evaluation); }}
                        disabled={linkingInProgress}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors cursor-pointer max-w-full"
                        title="Click to unlink lead"
                      >
                        <span className="flex-shrink-0">🔗</span>
                        <span className="truncate">{linkingInProgress ? '...' : (evaluation.linkedLeadName || linkedLeadNames[evaluation.linkedLeadId] || 'Linked')}</span>
                      </button>
                    ) : linkingEvalId === evaluation.id ? (
                      <div ref={leadPickerRef} className="absolute z-30 left-0 top-0 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-2" onClick={(e) => e.stopPropagation()}>
                        <div className="text-xs font-medium text-gray-600 mb-1">Link to a lead:</div>
                        <select
                          autoFocus
                          onChange={(e) => { if (e.target.value) handleLinkLeadToEval(evaluation.id, e.target.value); }}
                          disabled={linkingInProgress || loadingLeads}
                          className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          defaultValue=""
                        >
                          <option value="" disabled>{loadingLeads ? 'Loading leads...' : 'Choose a lead...'}</option>
                          {recentLeads
                            .filter(l => !l.linkedEvaluationId)
                            .map(l => (
                              <option key={l.id} value={l.id}>
                                {l.prospectName || l.prospectPhone || 'Unnamed'} — {l.timestamp?.toLocaleDateString() || 'N/A'}
                              </option>
                            ))}
                        </select>
                        <button
                          onClick={() => setLinkingEvalId(null)}
                          className="mt-1 text-xs text-gray-400 hover:text-gray-600"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); setLinkingEvalId(evaluation.id); }}
                        className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-green-600 transition-colors cursor-pointer"
                        title="Link to a lead"
                      >
                        🔗 Link
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2 text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      {evaluation.status === 'completed' ? (
                        <>
                          <button
                            onClick={() => setSelectedEval(evaluation)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                          View
                          </button>
                          <button
                            onClick={() => handleExportIndividual(evaluation)}
                            className="text-green-600 hover:text-green-900"
                          >
                          PDF
                          </button>
                        </>
                      ) : evaluation.status === 'error' ? (
                        <span className="text-red-600 text-xs">{evaluation.error || 'Failed'}</span>
                      ) : (
                        <span className="text-gray-400 text-xs">Processing...</span>
                      )}
                    
                      {/* Delete button */}
                      <button
                        onClick={() => handleDelete(evaluation)}
                        disabled={deletingId === evaluation.id}
                        className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Delete evaluation"
                        aria-label={`Delete evaluation for ${getDisplayName(evaluation)}`}
                      >
                        {deletingId === evaluation.id ? (
                          <LoadingSpinner size="small" inline />
                        ) : (
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {getExpirationBadge(getDaysRemaining(evaluation.expiresAt))}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Evaluation Report Modal */}
      {selectedEval && (
        <EvaluationReport
          evaluation={selectedEval}
          onClose={() => setSelectedEval(null)}
          onEvaluationUpdate={handleEvaluationUpdate}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => { setShowDeleteConfirm(false); setPendingDeleteEval(null); }}
        onConfirm={confirmDelete}
        title="Delete Evaluation?"
        message={pendingDeleteEval
          ? `Are you sure you want to delete this evaluation?\n\nSalesperson: ${pendingDeleteEval.salesperson}\nProspect: ${pendingDeleteEval.prospect}\n\nThis action cannot be undone.`
          : ''}
        confirmLabel="Delete"
        confirmVariant="danger"
      />
    </div>
  );
}

